import type { AssessmentSummary, ResultSummaryRow } from '@/lib/api';

/**
 * candidates.ts — how a *candidate* is derived, in the one place that derives it.
 *
 * **There is no candidates list endpoint.** A candidate is not a record in this
 * platform: `candidateRef` is an opaque identifier stamped on instances and
 * results, and the roster is whatever grouping those two feeds produce. That was
 * fine while the Candidates screen was the only place asking — the moment the
 * topbar search needed the same roster (X2), a second grouping would have been a
 * second answer to "who exists", disagreeing the first time either feed changed
 * shape.
 *
 * So this is lifted out of `pages/Candidates.tsx` unchanged rather than copied.
 * Callers that only have one feed pass `[]` for the other.
 *
 * NO PII, ever. `candidateRef` is the only thing about a person that this
 * platform stores or displays.
 */

/** Instances and results, grouped by the candidate they belong to. */
export interface CandidateRow {
  candidateRef: string;
  instances: AssessmentSummary[];
  results: ResultSummaryRow[];
}

export function groupByCandidate(
  instances: AssessmentSummary[],
  results: ResultSummaryRow[],
): CandidateRow[] {
  const rows = new Map<string, CandidateRow>();
  const row = (ref: string) => {
    if (!rows.has(ref)) rows.set(ref, { candidateRef: ref, instances: [], results: [] });
    return rows.get(ref)!;
  };

  for (const i of instances) row(i.candidateRef).instances.push(i);
  for (const r of results) row(r.candidateRef).results.push(r);

  return [...rows.values()].sort((a, b) => {
    // Most recently active first — a delivery view is about what is happening now.
    const latest = (c: CandidateRow) =>
      Math.max(0, ...c.instances.map((i) => new Date(i.createdAt).getTime()));
    return latest(b) - latest(a);
  });
}
